// @ts-nocheck
import './WikiBox.scss';

import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { bindActionCreators } from 'redux';

import { changeSearchType, setDismissMapOpenCard } from '../../../actions/app';
import { getPublicKey, setActiveCardFromWidget, setDetailedMapQuery, setIsMapTab } from '../../../actions/search';

import constants from '../../../constants';
import breakpoints from '../../../constants/breakpoints';
import companyConfigs from '../../../constants/company';
import searchTypes from '../../../constants/searchTypes';
import { validatePublicKey } from '../../../utils/helpers';
import AppleMap, { useMapkitScript } from '../AppleMaps';
import ExpandableText from '../ExpandableText/ExpandableText';
import WidgetCTA from '../WidgetCTA/WidgetCTA';
import WidgetTemplate from '../WidgetTemplate/WidgetTemplate';
import { ExternalLinks } from './ExternalLinks';
import WikiBoxInfoBoxItem from './WikiBoxInfoBoxItem';

enum MapPointTypes {
    Street = 'street',
    Country = 'country',
    City = 'city',
    Usstate = 'usstate',
    UnitedStates = 'unitedStates',
}

type WikiBoxProp = {
    openInNewTab: boolean;
    wikiBoxInfoBox?: {
        category: {
            original: string;
            applied: string;
        };
        created_at: number;
        last_parsed: number;
        rows: IWikiBoxInfoboxRow[];
        url: string;
    };
    encryptionSetting: boolean;
    publicKey?: string;
    publicKeyVersion?: string;
    publicKeyExpirationTime?: string;
    appLanguage: AppLanguageType;
    theme: ThemeType;
    'data-test': string;
    activeCardFromWidget?: number;
    changeSearchType: () => {};
    wikiBox: IWikiBox;
    wikiBoxInfoBoxNotFound?: boolean;
    getPublicKey: () => string;
    handleTabChange: () => {};
    makeSearch: () => {};
    onLoadCallback: () => {};
    publicKeyDomain?: string;
    responseTime: string;
    setActiveCardFromWidget: (number) => {};
    setDismissMapOpenCard: (boolean) => {};
    setIsMapTab: (boolean) => {};
    setDetailedMapQuery: (string) => {};
};

const maxWikiBoxHeight = 870;
const infoBoxMaxHeight = 400;

const WikiBox: FC<WikiBoxProp> = memo((props) => {
    const {
        openInNewTab,
        wikiBoxInfoBox,
        wikiBoxInfoBoxNotFound,
        handleTabChange,
        encryptionSetting,
        publicKey,
        publicKeyVersion,
        appLanguage,
        theme,
        responseTime,
        setIsMapTab,
        setActiveCardFromWidget,
        changeSearchType,
        activeCardFromWidget,
        wikiBox,
        setDismissMapOpenCard,
        makeSearch,
        setDetailedMapQuery,
        onLoadCallback,
        publicKeyExpirationTime,
        publicKeyDomain,
    } = props;
    const withAppleMapsDetailedView = companyConfigs.features.withAppleMapsDetailedView;

    const { t } = useTranslation('common');
    const [mapkitIsLoaded, mapkit] = useMapkitScript(appLanguage);
    const [isShowMore, setIsShowMore] = useState<boolean>(true);
    const [infoBoxHeight, setInfoBoxHeight] = useState<number>(0);
    const [wikiBoxHeight, setWikiBoxHeight] = useState<number>(0);
    const [imageType, setImageType] = useState<string | null>(null);
    const [initialized, setInitialized] = useState<boolean>(false);
    const [hideImageContainer, setHideImageContainer] = useState<boolean>(false);
    const [withMap, setWithMap] = useState<boolean>(false);
    const [mapPointType, setMapPointType] = useState<MapPointTypes>(MapPointTypes.Street);
    const [coordinates, setCoordinates] = useState<CoordinatesType[]>([]);
    const [shouldToggleChoplines, setShouldToggleChoplines] = useState<boolean>(false);
    const isMobile = !useMediaQuery({
        minWidth: breakpoints.minWidthMdBreakPoint,
    });
    const rowCountThreshold = isMobile ? 5 : 8;

    const displayShowMoreLessButton = useMemo(() => {
        return (
            wikiBoxHeight > maxWikiBoxHeight + 40 ||
            infoBoxHeight > infoBoxMaxHeight + 10 ||
            wikiBoxInfoBox?.rows?.length > rowCountThreshold
        );
    }, [wikiBoxInfoBox, wikiBoxHeight, infoBoxHeight, rowCountThreshold]);

    //Todo put the wikibox header in a own file
    useEffect(() => {
        if (wikiBox?.title === 'United States') {
            setWithMap(true);
            setMapPointType(MapPointTypes.UnitedStates);
            return;
        }

        if (wikiBox?.infobox?.category?.applied === 'country') {
            setWithMap(true);
            setMapPointType(MapPointTypes.Country);
            return;
        }

        if (wikiBox?.description) {
            let description = wikiBox.description;

            if (description.includes('is a historical ')) {
                return;
            }

            if (description.includes('is the capital')) {
                setWithMap(true);
                setMapPointType(MapPointTypes.City);
                return;
            }

            if (description.includes('is the largest city')) {
                setWithMap(true);
                setMapPointType(MapPointTypes.City);
                return;
            }

            if (description.includes('most populous city')) {
                setWithMap(true);
                setMapPointType(MapPointTypes.City);
                return;
            }
        }

        if (wikiBox?.infobox?.category?.applied === 'settlement') {
            let description = wikiBox.description;
            if (description.includes('is a state')) {
                setWithMap(true);
                setMapPointType(MapPointTypes.Usstate);
                return;
            }

            setWithMap(true);
            setMapPointType(MapPointTypes.City);
            return;
        }

        if (wikiBox?.infobox?.category?.applied === 'us-state') {
            setWithMap(true);
            setMapPointType(MapPointTypes.Usstate);
            return;
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (mapkitIsLoaded && withMap) {
            const searchObject = new mapkit.Search({
                language: 'en-GB',
                getsUserLocation: false,
                region: new mapkit.CoordinateRegion(new mapkit.Coordinate(13, -17)),
            });
            searchObject.search(wikiBox.title, (error, data) => {
                if (data && data.places && data.places.length) {
                    let corr = data.places[0].coordinate;
                    if (data.places.length > 1) {
                        const bestChoice = data.places.find((i) =>
                            i?.formattedAddress?.toLowerCase().includes(wikiBox.title?.toLowerCase()),
                        );
                        if (bestChoice) {
                            corr = bestChoice.coordinate;
                        }
                    }

                    setCoordinates([
                        {
                            coordinates: {
                                latitude: parseFloat(corr.latitude),
                                longitude: parseFloat(corr.longitude),
                            },
                        },
                    ]);
                }
            });
        }
        // eslint-disable-next-line
    }, [mapkitIsLoaded]);

    const textRef = useRef<HTMLElement | undefined>();
    const imgRef = useRef<HTMLImageElement | HTMLButtonElement | null>();
    const wikiBoxRef = useRef<HTMLElement | undefined>();
    const infoBoxRef = useRef<HTMLTableSectionElement | null>();

    const refreshItemHeightValues = useCallback(() => {
        setWikiBoxHeight(wikiBoxRef?.current?.clientHeight as number);
        setInfoBoxHeight(infoBoxRef?.current?.clientHeight as number);
    }, []);

    useEffect(() => {
        refreshItemHeightValues();

        if (onLoadCallback) {
            onLoadCallback();
        }
        if (initialized) {
            return;
        }
        if (encryptionSetting && !validatePublicKey(publicKey, publicKeyExpirationTime, publicKeyDomain)) {
            getPublicKey();
        }
        setInitialized(true);
    }, [
        encryptionSetting,
        initialized,
        isMobile,
        publicKey,
        refreshItemHeightValues,
        onLoadCallback,
        publicKeyExpirationTime,
        publicKeyDomain,
    ]);

    const toggleShowMore = () => {
        setIsShowMore((prevState) => !prevState);
        setShouldToggleChoplines(true);
        setTimeout(() => refreshItemHeightValues(), 0.5);
    };

    const onLoadImageHandler = () => {
        const { width, height } = imgRef.current?.getBoundingClientRect();
        setImageType(height > width ? 'portrait' : 'landscape');
    };

    const onErrorImageHandler = () => {
        setHideImageContainer(true);
    };

    const replaceImageUrl = (url) => {
        if (!url) {
            return url;
        }
        if (url.includes('upload.wikimedia.org')) {
            return url.replace('upload.wikimedia.org', 'wikimedia.tempest.com');
        }
        if (!url.includes('tempest.com')) {
            return constants.paths.imageSrcProxy + new URL(url).search;
        }
        return url;
    };

    const { title, description, image_url, url, external_links, images } = wikiBox;
    const findWiki = url?.includes('wiki');
    const imageUrl = image_url || (images?.bing?.length && images.bing[0]);
    const hasSubtitle = false;
    const withGallery = images?.bing && images?.bing?.length >= 3;
    const isColumnTopView = withGallery || withMap ? true : false;
    const isSingleImg = withGallery || withMap ? false : true;
    const galleryPicturesAmount = 3;

    const wikiBoxInfoBoxRows = useMemo(() => {
        if (isShowMore && displayShowMoreLessButton) {
            return wikiBoxInfoBox?.rows.slice(0, rowCountThreshold) || [];
        }

        return wikiBoxInfoBox?.rows || [];
    }, [isShowMore, displayShowMoreLessButton, rowCountThreshold, wikiBoxInfoBox]);

    const openMap = () => {
        if (withAppleMapsDetailedView) {
            setDetailedMapQuery(wikiBox.title);
            setDismissMapOpenCard(true);
            setIsMapTab(false);
            setActiveCardFromWidget(null);
            changeSearchType(searchTypes.map, 'WikiBox/WikiBox :269');
        }
    };

    const handleClick = (index, src) => {
        if (index !== activeCardFromWidget) {
            setIsMapTab(true);
            setActiveCardFromWidget(null);
            openMap();
        }
    };

    const getZoomLevel = () => {
        switch (mapPointType) {
            case MapPointTypes.Street:
                return constants.mapZoomLevel.street;
            case MapPointTypes.City:
                return constants.mapZoomLevel.city;
            case MapPointTypes.Country:
                return constants.mapZoomLevel.country;
            case MapPointTypes.UnitedStates:
                return constants.mapZoomLevel.unitedStates;
            case MapPointTypes.Usstate:
                return constants.mapZoomLevel.usstate;
        }
    };

    const handleShowMore = useCallback(() => {
        refreshItemHeightValues();
        setIsShowMore(false);
    }, [refreshItemHeightValues]);

    const handleInfoBoxChoplinesToggle = () => setShouldToggleChoplines(false);

    if (!(title && description)) {
        return null;
    }

    return (
        <WidgetTemplate.Wrapper>
            <WidgetTemplate.Paper
                callbackRef={(el) => {
                    wikiBoxRef.current = el;
                }}
                className={`wikiBox`}
                data-test={'WikiBox'}
                style={{
                    visibility: !imageUrl ? 'visible' : 'hidden',
                }}
            >
                <div className={`wikiBox__top${isColumnTopView ? ` wikiBox__top--column` : ``}`}>
                    {!hideImageContainer && (
                        <figure
                            className={`wikiBox__picture-wrapper${
                                imageType === 'portrait' ? ` wikiBox__picture-wrapper--portrait` : ``
                            }${isColumnTopView ? ` wikiBox__picture-wrapper--column` : ``}${
                                withGallery && galleryPicturesAmount
                                    ? ` wikiBox__picture-wrapper--${galleryPicturesAmount}`
                                    : ''
                            }`}
                        >
                            {isSingleImg && imageUrl && (
                                <img
                                    className={`wikiBox__picture${
                                        imageType === 'portrait' ? ' wikiBox__picture--portrait' : ''
                                    }`}
                                    src={replaceImageUrl(imageUrl)}
                                    alt={imageUrl.split('/').pop()}
                                    onClick={() => handleTabChange(searchTypes.images)}
                                    ref={(ref) => (imgRef.current = ref)}
                                    onError={onErrorImageHandler}
                                    onLoad={onLoadImageHandler}
                                    width={imageType === 'portrait' ? 98 : 117}
                                    /**
                                     * Todo: we need to find a way to determine the image's
                                     * height attribute correctly before it's being loaded.
                                     */
                                    //height="141"
                                />
                            )}
                            {coordinates.length > 0 && (
                                <>
                                    {imageUrl && (
                                        <button
                                            type="button"
                                            className={`wikiBox__map-picture`}
                                            style={{
                                                backgroundImage: `url(${replaceImageUrl(imageUrl)})`,
                                            }}
                                            onClick={() => handleTabChange(searchTypes.images)}
                                            ref={(ref) => (imgRef.current = ref)}
                                            onError={onErrorImageHandler}
                                        />
                                    )}
                                    <div className="wikiBox__map" onClick={openMap}>
                                        {mapkitIsLoaded && (
                                            <div className="wikiBox__map--wrapper">
                                                <AppleMap
                                                    places={coordinates}
                                                    mapOptions={{
                                                        showsMapTypeControl: false,
                                                    }}
                                                    clickHandler={handleClick}
                                                    theme={theme}
                                                    zoomLevel={getZoomLevel()}
                                                    mapkit={mapkit}
                                                    noAnnotationLabel={true}
                                                    style={
                                                        mapkitIsLoaded
                                                            ? {
                                                                  visibility: 'visible',
                                                                  position: 'absolute',
                                                                  right: '0',
                                                                  left: '0',
                                                                  top: '0',
                                                                  bottom: '0',
                                                              }
                                                            : // Hide interactive map offscreen in order to preload it
                                                              {
                                                                  visibility: 'hidden',
                                                                  position: 'absolute',
                                                                  right: '-50px',
                                                                  top: '50px',
                                                              }
                                                    }
                                                    responseTime={responseTime}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            {withGallery && !withMap && (
                                <>
                                    {images.bing.slice(0, 3).map((img, i) => {
                                        return (
                                            <img
                                                className="wikiBox__img"
                                                key={`wikiBox-img-gallary-${i}`}
                                                src={replaceImageUrl(img)}
                                                alt={img.split('/').pop()}
                                                onError={onErrorImageHandler}
                                                onClick={() => handleTabChange(searchTypes.images)}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </figure>
                    )}
                    <div className="wikiBox__title-block">
                        <h2 className="wikiBox__title">{title}</h2>
                        {hasSubtitle && <div className="wikiBox__subtitle">Subheading</div>}
                        {/*TODO: add real data*/}
                    </div>
                </div>
                <WidgetTemplate.Block className="wikiBox__block" ref={textRef}>
                    <ExpandableText text={description} className="wikiBox__text" />
                </WidgetTemplate.Block>
                {!!wikiBoxInfoBoxRows.length && wikiBoxInfoBox && !wikiBoxInfoBoxNotFound && (
                    <WidgetTemplate.Block className="wikiBox__block">
                        <table className={isShowMore ? 'wikiBox__enhanced fit' : 'wikiBox__enhanced'}>
                            <tbody ref={infoBoxRef}>
                                {wikiBoxInfoBoxRows.map((item, i) => {
                                    return (
                                        <WikiBoxInfoBoxItem
                                            data={item}
                                            openInNewTab={openInNewTab}
                                            key={i}
                                            shouldToggleChoplines={shouldToggleChoplines}
                                            onChoplinesToggled={handleInfoBoxChoplinesToggle}
                                            encryptionDetails={{
                                                encryptionSetting,
                                                publicKey,
                                                publicKeyVersion,
                                            }}
                                            makeSearch={makeSearch}
                                            onShowMore={handleShowMore}
                                            topicTitle={wikiBox.title}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                        {displayShowMoreLessButton && (
                            <WidgetCTA
                                className="wikiBox__cta"
                                buttonType="vertical"
                                buttonSize="full"
                                onClick={toggleShowMore}
                                isRotateIcon={!isShowMore}
                            >
                                {isShowMore ? t('see-more', 'See more') : t('see-less', 'See less')}
                            </WidgetCTA>
                        )}
                    </WidgetTemplate.Block>
                )}
                <WidgetTemplate.Block className="wikiBox__block">
                    {(findWiki || external_links) && (
                        <ExternalLinks links={external_links} wikiUrl={url} openInNewTab={openInNewTab} />
                    )}
                </WidgetTemplate.Block>
            </WidgetTemplate.Paper>
            <WidgetTemplate.Footer withFeedback={true} />
        </WidgetTemplate.Wrapper>
    );
});
function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getPublicKey,
            setIsMapTab,
            setActiveCardFromWidget,
            changeSearchType,
            setDismissMapOpenCard,
            setDetailedMapQuery,
        },
        dispatch,
    );
}

const mapStateToProps = (state, props) => ({
    wikiBoxInfoBox: state.search.wikiBoxInfoBox,
    wikiBoxInfoBoxNotFound: state.search.wikiBoxInfoBoxNotFound,
    publicKey: state.search.publicKey,
    publicKeyVersion: state.search.publicKeyVersion,
    publicKeyExpirationTime: state.search.publicKeyExpirationTime,
    publicKeyDomain: state.search.publicKeyDomain,
    encryptionSetting: state.app.encryptionSetting,
    appLanguage: state.app.appLanguage,
    theme: state.app.theme,
    responseTime: state.search.responseTime,
    activeCardFromWidget: state.search.activeCardFromWidget,
});

export default connect(mapStateToProps, mapDispatchToProps)(WikiBox);
